import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useTypedSelector, useTypedDispatch } from "App/Store";
import { showLoader, hideLoader } from "redux/action/loaderAction";
import { useSocket } from "App/socketContext";
import { Button, Modal } from "rsuite";
import {
  getTournamentMatchChatMessages,
  getTournamentPlayOffBattleRoyale,
} from "core/services/Tournament";
import { convertFileToBase64 } from "core/utils/Image";
import { uniqueId } from "core/utils/StringHelper";
import { multipleupload } from "core/services/Upload";
import TournamentChatMessage from "components/TournamentGroupChat/TournamentMessage";
import TournamentChatInput from "components/TournamentGroupChat/TournamentChatInput";
import TournamentChatTeamDetails from "components/TournamentGroupChat/TournamentChatTeamDetails";
import ChatImagePreview from "components/TournamentGroupChat/MatchChatImagePreview";
import { userDetails } from "redux/action/userDetailsAction";
import TournamentGroupChatHeader from "components/TournamentGroupChat/TournamentGroupChatHeader";
import { useTeamIdToColor } from "shared/hooks/useTeamIdToColor";
import { TeamDetailed } from "core/models/Tournament";

export interface Message {
  id: string;
  text: string;
  time: string;
  sender: "me" | "other";
  date: string;
  profileImage?: string;
  name?: string;
  role?: string;
  createdAt?: string;
  metadata?: any;
  attachments?: string[];
  senderId: string;
  deletedBy?: string;
  isDeleted?: boolean;
  teamName?: string
}

interface UrlParams {
  groupId: string;
}

const PAGE_SIZE = 20;

export default function PlayoffGroupChatPage() {
  const { groupId } = useParams<UrlParams>();
  const socket = useSocket();
  const { user } = useTypedSelector((state) => state.userDetails);
  const dispatch = useTypedDispatch();

  const [playOff, setPlayOff] = useState<any>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [sendMessageLoader, setSendMessageLoader] = useState(false);
  const [images, setImages] = useState<any[]>([]);
  const [selectedImage, setSelectedImage] = useState<any | null>(null);
  const [imageIndex, setImageIndex] = useState(0);
  const uploadRef = useRef<HTMLInputElement>(null);
  const [viewImage, setViewImage] = useState("");
  const [viewModal, setViewModal] = useState(false);
  const [inputText, setInputText] = useState("");
  const didFetchMessages = useRef(false);
  const [isDropDown, setIsDropDown] = useState<{
    open: boolean;
    messageId: string;
  }>({
    open: false,
    messageId: "",
  });
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState({
    open: false,
    messageId: "",
  });

  const teamNameByUserId = useMemo(() => {
    if (!playOff?.teamsDetailed) return {};
  
    const map: Record<string, string> = {};
  
    playOff.teamsDetailed.forEach((team: TeamDetailed) => {
      team.users?.forEach((u) => {
        if (u.id) {
          map[u.id.toString()] = team.teamName;
        }
      });
    });
  
    return map;
  }, [playOff]);

  function openDropdownModal(messageId: string) {
    setIsDropDown({ open: true, messageId });
  }

  function closeDropdownModal() {
    setIsDropDown({ open: false, messageId: "" });
  }

  function openDeleteModal(messageId: string) {
    setIsDeleteModalOpen({ open: true, messageId: messageId });
  }

  useEffect(() => {
    dispatch(userDetails());
  }, [dispatch]);

  useEffect(() => {
    if (!groupId) return;

    const fetchData = async () => {
      try {
        dispatch(showLoader());
        const res = await getTournamentPlayOffBattleRoyale(groupId);
        if (res?.status && res?.data?.playoffData) {
          setPlayOff(res.data.playoffData);
        }
      } catch (err) {
        console.error("Error fetching playoff:", err);
      } finally {
        dispatch(hideLoader());
      }
    };

    fetchData();
  }, [groupId, dispatch]);

  useEffect(() => {
    if (!socket) return;
    if (!groupId) return;

    socket.emit("joinRoom", { roomId: groupId });

    return () => {
      socket.emit("leaveRoom", { roomId: groupId });
    };
  }, [socket, groupId]);

  const fetchMessages = async (currentPage: number) => {
    if (!groupId || isLoading) return;

    setIsLoading(true);
    try {
      const response = await getTournamentMatchChatMessages({
        roomId: groupId,
        page: currentPage,
        limit: PAGE_SIZE,
      });

      const mappedMessages: Message[] =
        response?.messages?.map((val: any) => ({
          sender: user?.id === val.sender ? "me" : "other",
          date: new Date(val.createdAt).toISOString().split("T")[0],
          id: val._id.toString(),
          text: val.content,
          time: new Date(val.createdAt).toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
          }),
          profileImage: val.senderMedia,
          name: val.senderName,
          role: ["admin", "hub-admin", "moderator"].includes(val.role)
            ? "admin"
            : "user",
          createdAt: val.createdAt,
          metadata: val.metadata,
          attachments: val.attachments,
          senderId: val.sender,
          isDeleted: val.isDeleted || false,
          deletedBy: val.deletedBy || "",
        })) ?? [];

      setMessages((prev) => [...prev, ...mappedMessages]);
      setHasMore(Boolean(response?.hasMore));
      setPage(currentPage);
    } catch (err) {
      console.error("Error fetching messages:", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (socket) {
      if (!didFetchMessages.current && groupId) {
        didFetchMessages.current = true;
        fetchMessages(1);
      }
    }
  }, [socket]);

  const loadMoreMessages = () => {
    if (!isLoading && hasMore) {
      fetchMessages(page + 1);
    }
  };

  useEffect(() => {
    if (!user || !socket) return;

    socket.on("newMessageGroup", (message: any) => {
      try {
        const newMessage: Message = {
          sender:
            user?.id?.toString() === message?.sender?.toString()
              ? "me"
              : "other",
          date: new Date(message?.createdAt).toISOString().slice(0, 10),
          id: message?.id.toString(),
          text: message?.content,
          time: new Date(message?.createdAt).toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
          }),
          profileImage: message?.senderMedia,
          name: message?.senderName,
          role: ["admin", "hub-admin", "moderator"].includes(message?.role)
            ? "admin"
            : "user",
          createdAt: message?.createdAt,
          metadata: message?.metaData,
          attachments: message?.attachments,
          senderId: message?.sender,
          isDeleted: message?.isDeleted || false,
          deletedBy: message?.deletedBy || "",
        };

        setMessages((prevMessages) => [newMessage, ...prevMessages]);
      } catch (err) {
        console.error("Error handling new message:", err);
      }
    });

    return () => {
      socket.off("newMessageGroup");
    };
  }, [user, socket]);

  const handleFileSelect = async (files: File[]) => {
    const newImages = await Promise.all(
      files.map(async (file) => ({
        url: await convertFileToBase64(file),
        file,
        id: uniqueId("image"),
      }))
    );
    setImages((prev) => [...prev, ...newImages]);
    setSelectedImage(newImages[0]);
  };

  const handleRemoveImage = (index: number) => {
    const updated = images.filter((_, i) => i !== index);
    setImages(updated);
    if (updated.length === 0) {
      setSelectedImage(null);
      setImageIndex(0);
    } else {
      setSelectedImage(updated[Math.max(0, index - 1)]);
      setImageIndex(Math.max(0, index - 1));
    }
  };

  const handleSendMessage = async (text?: string) => {
    if (!playOff) return;
    if (text?.trim() === "" && images?.length === 0) return;

    let type: "text" | "image" | "text-image" = "text";
    if (text?.trim() !== "" && images.length > 0) type = "text-image";
    else if (text?.trim() !== "") type = "text";
    else if (images.length > 0) type = "image";

    setSendMessageLoader(true);
    let attachments: string[] = [];
    if (images.length > 0) {
      const formData = new FormData();
      images.forEach((imageObj) => {
        if (imageObj.file) formData.append("file", imageObj.file);
      });
      const res = await multipleupload(formData);
      if (res.status && res.data) {
        attachments = res.data?.images;
      }
    }

    socket?.emit("sendMessageGroup", {
      roomId: playOff._id,
      messageType: type,
      content: text,
      attachments,
      metadata: {
        teamId: playOff?.teamsDetailed?.find((tD: any) =>
          tD.users?.some((u: any) => u?.id === user?.id)
        )?.teamId,
        captain: playOff?.teamsDetailed
          ?.find((tD: any) => tD?.users?.some((u: any) => u?.id === user?.id))
          ?.users.find((u: any) => u?.id === user?.id)?.role,
      },
    });

    setImages([]);
    setSelectedImage(null);
    setSendMessageLoader(false);
    setInputText("");
  };

  const handleViewImage = (image: string) => {
    setViewImage(image);
    setViewModal(true);
  };

  const closeViewModal = () => {
    setViewImage("");
    setViewModal(false);
  };

  const closeDeleteModal = () => {
    setIsDeleteModalOpen({ open: false, messageId: "" });
  };

  const handleConfirmDeleteMessage = () => {
    socket?.emit("deleteMessage", {
      roomId: playOff._id,
      messageId: isDeleteModalOpen.messageId,
      deletedBy: user?.id,
    });

    setIsDropDown({ open: false, messageId: "" });
    setIsDeleteModalOpen({ open: false, messageId: "" });
  };

  useEffect(() => {
    socket?.on("messageDeleted", (data) => {
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id?.toString() === data.id?.toString()
            ? {
                ...msg,
                text: "",
                attachments: [],
                isDeleted: data.isDeleted,
                deletedBy: data.deletedBy,
              }
            : msg
        )
      );
    });

    return () => {
      socket?.off("messageDeleted");
    };
  }, [socket]);

  const teamIdToColor = useTeamIdToColor(playOff?.teams);

  if (!playOff) return null;

  return (
    <div>
      <div className="tournamnet-msg-team-wrapper">
        <div className="tournamnet-msg-wrapper">
          <TournamentGroupChatHeader
            groupName={playOff?.groupName || "Group Chat"}
            roundSequence={playOff?.roundSequence || 0}
            teamIdToColor={teamIdToColor}
          />

          <div className="tournamnet-msg">
            <TournamentChatMessage
              messages={messages}
              hasMore={hasMore}
              loadMoreMessages={loadMoreMessages}
              isHeight={true}
              isLoading={isLoading}
              teamIdToClass={teamIdToColor}
              handleViewImage={handleViewImage}
              isDropDown={isDropDown}
              openDropdownModal={openDropdownModal}
              closeDropdownModal={closeDropdownModal}
              openDeleteModal={openDeleteModal}
              isGroupChat={true}
              teamNameByUserId={teamNameByUserId}

            />

            {images.length > 0 && (
              <ChatImagePreview
                selectedImage={selectedImage?.url}
                imageUrl={images}
                imageIndex={imageIndex}
                onChangeSelectedImage={(idx: number) => {
                  setSelectedImage(images[idx]);
                  setImageIndex(idx);
                }}
                onRemoveImage={(idx: number) => handleRemoveImage(idx)}
                isEditDisable={false}
                onDisableImagePreview={() => {
                  setImages([]);
                  setSelectedImage(null);
                  setImageIndex(0);
                }}
                uploadRef={uploadRef}
              />
            )}
          </div>

          <TournamentChatInput
            placeholder="Write a message..."
            // className="message-input"
            onSendMessage={handleSendMessage}
            uploadRef={uploadRef}
            onFileSelect={handleFileSelect}
            sendMessageLoader={sendMessageLoader}
            setInputText={setInputText}
            inputText={inputText}
          />
        </div>

        <TournamentChatTeamDetails
          teamsDetailed={playOff?.teamsDetailed}
          teamIdToClass={teamIdToColor}
          isGroupChat={true}
        />
        <Modal
          overflow
          backdrop="static"
          show={viewModal}
          onHide={closeViewModal}
          size="sm"
        >
          <Modal.Header>
            <Modal.Title>Image Preview</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className={"view-modal"}>
              <img
                src={viewImage}
                alt="loaded img"
                className={"view-modal-img"}
              />
            </div>
          </Modal.Body>
        </Modal>

        <Modal
          overflow
          backdrop="static"
          show={isDeleteModalOpen.open}
          onHide={closeDeleteModal}
          size="sm"
        >
          <Modal.Header>
            <Modal.Title>Delete Message</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="delete-modal-content">
              <p className="delete-text">
                Are you sure you want to delete this message?
              </p>
              <div className="delete-actions">
                <Button
                  className="cancel-btn"
                  onClick={closeDeleteModal}
                  appearance="ghost"
                  color="blue"
                >
                  Cancel
                </Button>
                <Button
                  className="delete-btn"
                  appearance="primary"
                  color="red"
                  onClick={handleConfirmDeleteMessage}
                >
                  Delete
                </Button>
              </div>
            </div>
          </Modal.Body>
        </Modal>
      </div>
    </div>
  );
}
